<template>
  <div class="tdt">
    <!-- Summary strip -->
    <div class="tdt-summary">
      <div class="tdt-sum tdt-sum--total">
        <span class="tdt-sum__k">{{ $t('trade.totalTrades') }}</span>
        <span class="tdt-sum__v">{{ tradeSummary.total }}</span>
      </div>
      <div class="tdt-sum tdt-sum--up">
        <span class="tdt-sum__k">{{ $t('trade.wins') }} · {{ tradeSummary.winners }}</span>
        <span class="tdt-sum__v">{{ tradeSummary.winSum | numberFormatter }} {{ currencySymbol }}</span>
      </div>
      <div class="tdt-sum tdt-sum--down">
        <span class="tdt-sum__k">{{ $t('trade.losses') }} · {{ tradeSummary.losers }}</span>
        <span class="tdt-sum__v">{{ tradeSummary.lossAbs | numberFormatter }} {{ currencySymbol }}</span>
      </div>
      <div class="tdt-sum" :class="tradeSummary.net >= 0 ? 'tdt-sum--up' : 'tdt-sum--down'">
        <span class="tdt-sum__k">{{ $t('trade.net') }}</span>
        <span class="tdt-sum__v">{{ tradeSummary.net | numberFormatter }} {{ currencySymbol }}</span>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="tdt-toolbar">
      <div class="tdt-toolbar__left">
        <b-button v-if="isHistory !== true && hasOneOpenSymbolSelected" size="sm"
                  id="set-representative" variant="danger" class="tdt-btn"
                  @click="openMassCloseOrder" v-bind:disabled="!hasOneOpenSymbolSelected">
          <i class="fa-solid fa-xmark mr-50"></i>
          <span class="description">{{ $t('operations.close') }}</span>
          <span class="tdt-count">{{ selectedTrades.length }}</span>
        </b-button>
        <!-- Bulk delete: any selection, any mix of symbols -->
        <b-button v-if="isHistory !== true && selectedTrades.length" size="sm"
                  variant="outline-danger" class="tdt-btn"
                  @click="openBulkDelete">
          <i class="fa-solid fa-trash-can mr-50"></i>
          <span class="description">{{ $t('operations.delete') }}</span>
          <span class="tdt-count">{{ selectedTrades.length }}</span>
        </b-button>
      </div>
      <div class="tdt-toolbar__right">
        <b-dropdown variant="outline-secondary" size="sm" class="tdt-sort" v-b-tooltip :title="$t('operations.sortBy')">
          <template #button-content>
            <i v-if="sortedTradesBy[sortBy].order === 1" class="fa-solid fa-arrow-up-short-wide mr-50"></i>
            <i v-else class="fa-solid fa-arrow-down-wide-short mr-50"></i>
            <b>{{ $t(`trade.${sortedTradesBy[sortBy].objKey}`) }}</b>
          </template>
          <template v-for="sortKey in Object.keys(sortedTradesBy)">
            <b-dropdown-divider v-if="sortKey === 'none'" v-bind:key="sortKey"></b-dropdown-divider>
            <b-dropdown-item v-else v-bind:key="sortKey" @click="()=> sortBy=sortKey" :active="sortBy === sortKey">
              <i v-if="sortedTradesBy[sortKey].order === 1" class="fa-solid fa-arrow-up-short-wide mr-50"></i>
              <i v-else class="fa-solid fa-arrow-down-wide-short mr-50"></i>
              {{ sortedTradesBy[sortKey].objKey }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </div>
    </div>

    <!-- Trades list -->
    <perfect-scrollbar class="tdt-scroll">
      <div v-for="(trade,index) in sortedTrades" :key="index"
           class="tdt-card"
           :class="[trade.command === 'BUY' ? 'is-buy' : 'is-sell',
                    { 'is-selected': isHistory !== true && selectedTradesIds.includes(trade.order) }]">

        <!-- header: direction + symbol + order  ·  profit -->
        <div class="tdt-card__head">
          <div class="tdt-card__id">
            <b-checkbox v-if="isHistory !== true" :value="trade.order" v-model="selectedTradesIds" class="tdt-check"/>
            <span class="tdt-dir" :class="trade.command === 'BUY' ? 'is-buy' : 'is-sell'">
              <i :class="{'fa-solid fa-arrow-trend-up': trade.command === 'BUY', 'fa-solid fa-arrow-trend-down': trade.command === 'SELL'}"></i>
              {{ trade.command }}
            </span>
            <span class="tdt-symbol">{{ trade.symbolName }}</span>
            <span class="tdt-order">#{{ trade.order }}</span>
          </div>
          <div class="tdt-card__profit" :class="trade.profit >= 0 ? 'is-up' : 'is-down'">
            {{ trade.profit | numberFormatter() }} {{ $t(`currencies.${account.currency}.symbol`) }}
            <i v-if="trade.notUpdated" v-b-tooltip v-bind:title="`Profit not updated`" class="fa-regular fa-circle-xmark tdt-warn"></i>
          </div>
        </div>

        <!-- time range -->
        <div class="tdt-card__time">
          <i class="fa-regular fa-clock mr-50"></i>
          {{ trade.openTime | momentDateTimeSeconds }}
          <template v-if="!trade.isOpen">
            <i class="fa-solid fa-arrow-right-long mx-50"></i>{{ trade.closeTime | momentDateTimeSeconds }}
          </template>
        </div>

        <!-- stats -->
        <div class="tdt-card__stats">
          <div class="tdt-stat"><span class="tdt-stat__k">{{ $t('trade.lots') }}</span><span class="tdt-stat__v">{{ trade.lots }}</span></div>
          <div class="tdt-stat"><span class="tdt-stat__k">{{ $t('trade.openPrice') }}</span><span class="tdt-stat__v">{{ trade.openPrice }}</span></div>
          <div class="tdt-stat">
            <span class="tdt-stat__k">{{ trade.isOpen ? $t('trade.currentPrice') : $t('trade.closePrice') }}</span>
            <span class="tdt-stat__v">{{ trade.closePrice }}</span>
          </div>
          <div class="tdt-stat"><span class="tdt-stat__k">{{ $t('trade.commission') }}</span><span class="tdt-stat__v">{{ trade.commission }}</span></div>
          <div class="tdt-stat"><span class="tdt-stat__k">{{ $t('trade.margin') }}</span><span class="tdt-stat__v">{{ trade.margin | numberFormatter }}</span></div>
          <div class="tdt-stat"><span class="tdt-stat__k">{{ $t('trade.taxes') }}</span><span class="tdt-stat__v">{{ trade.taxes }}</span></div>
        </div>

        <!-- actions -->
        <div class="tdt-card__actions">
          <b-button size="sm" variant="outline-warning" class="tdt-act" @click="editTradeRequest(trade)">
            <i class="fa-solid fa-pen mr-50"></i>{{ $t('operations.edit') }}
          </b-button>
          <b-button size="sm" variant="outline-danger" class="tdt-act"
                    v-bind:readonly="loading.closingTrade[trade.orderId] === true"
                    @click="()=>deleteTradeRequest(trade)">
            <i class="fas fa-cog fa-spin mr-50" v-if="loading.closingTrade[trade.orderId] === true"></i>
            <i class="fa-solid fa-trash-can mr-50" v-else></i>{{ $t('operations.delete') }}
          </b-button>
          <b-button v-if="isHistory !== true" size="sm" variant="success" class="tdt-act"
                    v-bind:readonly="loading.closingTrade[trade.orderId] === true"
                    @click="()=>closeTradeRequest(trade)">
            <i class="fas fa-cog fa-spin mr-50" v-if="loading.closingTrade[trade.orderId] === true"></i>
            <i class="fa-solid fa-xmark mr-50" v-else></i>{{ $t('operations.close') }}
          </b-button>
        </div>
      </div>

      <!-- empty state -->
      <div v-if="sortedTrades.length === 0" class="tdt-empty">
        <i class="fa-solid fa-chart-line"></i>
        <span>{{ $t('operations.noData') }}</span>
      </div>
    </perfect-scrollbar>

    <trade-mass-close-order-modal ref="trade-mass-close-order-modal" @close="refreshTables" @changed="refreshTables"/>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase'
import TradeMassCloseOrderModal from '@/components/modal/TradeMassCloseOrderModal.vue'

export default {
  name: 'TradeDetailsTab',
  components: { TradeMassCloseOrderModal },
  props:['trades','account', 'isHistory'],
  mixins:[mixinBase],
  data(){
    return {
      sortBy: 'openTime-desc',
      loading:{
        closingTrade:{}
      },
      selectedTradesIds:[],
    }
  },
  computed:{
    selectedTrades(){
      return (this.trades || []).filter(t => this.selectedTradesIds.includes(t.order))
    },
    hasOneOpenSymbolSelected(){
      let uniqueSymbols = new Set(this.selectedTrades.filter(o => o.isOpen).map(t => t.symbolId))
      return uniqueSymbols.size === 1 && this.selectedTrades.length > 0
    },
    sortedTradesBy(){
      return {
        'orderId-desc':    { objKey: 'orderId',     order: -1 ,  key:'orderId-desc'  },
        'openTime-desc':   { objKey: 'openTime',    order: -1 ,  key:'openTime-desc' },
        'closeTime-desc':  { objKey: 'closeTime',   order: -1 ,  key:'closeTime-desc' },
        'symbol-desc':     { objKey: 'symbol',      order: -1 ,  key:'symbol-desc'   },
        'profit-desc':     { objKey: 'profit',      order: -1 ,  key:'profit-desc'   },
        'none':{},
        'orderId-asc':     { objKey: 'orderId',     order: 1 ,   key:'orderId-asc'   },
        'openTime-asc':    { objKey: 'openTime',    order: 1 ,   key:'openTime-asc'  },
        'closeTime-asc':   { objKey: 'closeTime',   order: 1 ,   key:'closeTime-asc'  },
        'symbol-asc':      { objKey: 'symbol',      order: 1 ,   key:'symbol-asc'    },
        'profit-asc':      { objKey: 'profit',      order: 1 ,   key:'profit-asc'    },
      }
    },
    sortedTrades() {
      let sort = this.sortedTradesBy[this.sortBy]
      return (this.trades || []).sort((a, b) => {

        if (a[sort.objKey] < b[sort.objKey]) {
          return (-1 * sort.order)
        }
        if (a[sort.objKey] > b[sort.objKey]) {
          return (1 * sort.order)
        }
        return 0
      })
    },
    // Read-only summary derived from the trades prop (display only).
    currencySymbol() {
      return this.account && this.account.currency ? this.$t(`currencies.${this.account.currency}.symbol`) : ''
    },
    tradeSummary() {
      let winners = 0, losers = 0, winSum = 0, lossSum = 0, net = 0
      ;(this.trades || []).forEach(t => {
        const p = Number(t.profit) || 0
        net += p
        if (p > 0) { winners++; winSum += p }
        else if (p < 0) { losers++; lossSum += p }
      })
      return {
        total: (this.trades || []).length,
        winners, losers, winSum, lossSum,
        lossAbs: Math.abs(lossSum),
        net,
      }
    },
  },
  methods:{
    newTradeRequest(){
      this.$emit('newTradeRequest')
    },
    editTradeRequest(trade){
      let $this = this;
      // setTimeout(()=>this.$refs.closedTradesTab.$el.scrollTop = 0, 100)
      setTimeout(()=>$this.$emit('editTradeRequest',trade), 100)

    },
    closeTradeRequest(trade){
      let $this = this
      this.loading.closingTrade[trade.order] = false
      this.$emit('closeTradeRequest', { trade, callback: () => $this.loading.closingTrade[trade.order] = false })
    },
    deleteTradeRequest(trade){
      let $this = this
      this.showPrompt(this.$t('trade.deleteTrade'), this.$t('trade.deleteTradeConfirmation', {symbol:trade.symbolId, orderId:trade.order}), () => {
        $this.loading.closingTrade[trade.order] = true
        $this.$emit('deleteTradeRequest', { trade, callback: () => $this.loading.closingTrade[trade.order] = false })
      })
      this.loading.closingTrade[trade.order] = false
      // this.$emit('closeTradeRequest', { trade, callback: () => $this.loading.closingTrade[trade.orderId] = false })
    },
    openMassCloseOrder() {
      if (!this.hasOneOpenSymbolSelected) {
        return
      }
      this.$refs['trade-mass-close-order-modal'].showModal(this.selectedTrades.map(o => ({
            order: o.order,
            account: o.account
          })), this.selectedTrades[0].symbolId)
      this.selectedTradesIds = [];
    },
    // Bulk delete: the parent owns deleteOrder + the account login + the
    // refresh, so hand the selection up (same pattern as the single delete).
    openBulkDelete() {
      if (!this.selectedTrades.length) {
        return
      }
      let $this = this
      this.$emit('bulkDeleteRequest', {
        trades: this.selectedTrades,
        callback: () => { $this.selectedTradesIds = [] },
      })
    },
  }
}
</script>

<style lang="scss">
.tdt {
  --tdt-bg: #ffffff;
  --tdt-surface: #f8f8fc;
  --tdt-border: #ebe9f1;
  --tdt-text: #4b4763;
  --tdt-strong: #171430;
  --tdt-label: #9e9baf;
  --tdt-muted: #b4b4c1;
  --tdt-up: #1f9d57;
  --tdt-down: #e03e3f;
}
.dark-layout .tdt {
  --tdt-bg: #283046;
  --tdt-surface: #212536;
  --tdt-border: #3b4253;
  --tdt-text: #d8dae0;
  --tdt-strong: #ffffff;
  --tdt-label: #838798;
  --tdt-muted: #626878;
  --tdt-up: #3ed07f;
  --tdt-down: #ff6b6c;
}

/* Toolbar */
.tdt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}
.tdt-toolbar__right { display: flex; align-items: center; gap: 0.5rem; }
.tdt-btn { border-radius: 8px; font-weight: 600; }
.tdt-count {
  display: inline-block;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  padding: 0 0.45rem;
  margin-left: 0.4rem;
  font-size: 0.72rem;
  font-weight: 800;
}
.tdt-sort .dropdown-toggle { border-radius: 8px; }

/* Summary strip */
.tdt-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}
.tdt-sum {
  flex: 1 1 96px;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid var(--tdt-border);
  border-radius: 10px;
  background: var(--tdt-surface);
}
.tdt-sum__k {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
  color: var(--tdt-label);
}
.tdt-sum__v {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--tdt-strong);
  font-variant-numeric: tabular-nums;
}
.tdt-sum--total .tdt-sum__v { color: #7367f0; }
.tdt-sum--up .tdt-sum__v { color: var(--tdt-up); }
.tdt-sum--down .tdt-sum__v { color: var(--tdt-down); }

/* Scroll area — 60% of the viewport height */
.tdt-scroll {
  position: relative;
  max-height: 60vh;
  min-height: 200px;
  padding: 2px 8px 2px 2px;
}

/* Trade card */
.tdt-card {
  position: relative;
  background: var(--tdt-bg);
  border: 1px solid var(--tdt-border);
  border-left: 3px solid var(--tdt-muted);
  border-radius: 10px;
  padding: 0.5rem 0.7rem;
  margin-bottom: 0.4rem;
  transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;

  &.is-buy { border-left-color: var(--tdt-up); }
  &.is-sell { border-left-color: var(--tdt-down); }
  &:hover { box-shadow: 0 8px 20px -12px rgba(20, 24, 50, 0.45); transform: translateY(-1px); }
  &.is-selected {
    border-color: #7367f0;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.18);
  }
}
.tdt-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.tdt-card__id {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex-wrap: wrap;
}
.tdt-check { margin: 0; }
.tdt-dir {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  &.is-buy { color: var(--tdt-up); background: rgba(40, 199, 111, 0.13); }
  &.is-sell { color: var(--tdt-down); background: rgba(234, 84, 85, 0.13); }
}
.tdt-symbol { font-weight: 800; font-size: 1.02rem; color: var(--tdt-strong); }
.tdt-order { font-size: 0.76rem; color: var(--tdt-label); font-weight: 600; font-family: 'SFMono-Regular', Consolas, monospace; }
.tdt-card__profit {
  font-weight: 800;
  font-size: 1.02rem;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  &.is-up { color: var(--tdt-up); }
  &.is-down { color: var(--tdt-down); }
}
.tdt-warn { color: #ff9f43; margin-left: 0.25rem; font-size: 0.8rem; }
.tdt-card__time { font-size: 0.82rem; color: var(--tdt-text); margin-top: 0.25rem; font-weight: 600; }
.tdt-card__time i { color: var(--tdt-muted); }
.tdt-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.85rem;   // tight — columns sit next to each other, not stretched
  margin-top: 0.4rem;
  padding-top: 0.4rem;
  border-top: 1px dashed var(--tdt-border);
}
.tdt-stat { display: flex; flex-direction: column; flex: 0 0 auto; min-width: 56px; }
.tdt-stat__k {
  font-size: 0.64rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--tdt-label);
  font-weight: 700;
}
.tdt-stat__v {
  font-size: 0.92rem;
  color: var(--tdt-strong);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tdt-card__actions {
  display: flex;
  gap: 0.35rem;
  justify-content: flex-end;
  margin-top: 0.45rem;
  flex-wrap: wrap;
}
.tdt-act { border-radius: 8px; font-weight: 600; }

.tdt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 2.5rem 1rem;
  color: var(--tdt-muted);
  i { font-size: 1.9rem; opacity: 0.5; }
}
</style>
